/*
  Cart milestones (stage 1: free shipping; stage 2: the free gift): the progress bar, the
  upsell row and the gift panel, in the cart drawer and on /cart. The markup comes from
  snippets/cart-milestones-resolver.liquid (the rules:
  mms-shopify-dashboard/docs/cart-milestones/spec.md). This script only re-renders it and
  wires the cards and the gift picker; the gift's cart enforcement is in assets/mms-cart.js.

  Re-render: on each mms:cart-changed (assets/mms-cart.js: one event per actual change,
  after one /cart.js read) it makes ONE Section Rendering API request
  (?sections=cart-milestones) and copies each [data-cm-part="bar|upsell|gift"] from the
  response into every mount with the same part (drawer header, drawer list, drawer
  footer, /cart). Out-of-order responses are dropped.

  Cards: image and title open quick-look on the card's variant with an "Add to cart"
  action; Add adds that variant through MMSCart.addItems, then sends one "add" event
  (surface cart, rule = offer handle, the cart totals, the cart token; see
  MMSCart.sendAddEvents) and fires cart:build so Impulse redraws its line items. Errors
  show on the card.

  Random order: every card renders hidden; each render shuffles them and shows the
  offer's max shown (data-max-shown).

  Impressions: surface "cart", at most once per browser session per offer handle
  (sessionStorage), when the upsell row is at least 50% visible (drawer open, or on
  /cart). Never in the theme editor. Payload: offer_handle, shown_product_ids (the
  visible cards, in display order), cart_total_cents, remaining_cents.
*/
(function () {
  'use strict';

  if (window.MMSCartMilestones) return;

  var SECTION_ID = 'cart-milestones';
  var PARTS = ['bar', 'upsell', 'gift'];
  var IMPRESSION_KEY = 'mms_cart_imp:';
  var GENERIC_ERROR = 'Sorry, this item couldn’t be added.';
  var renderSeq = 0;
  var sentThisPage = {};

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  // ---------- Re-render ----------

  function render() {
    var seq = ++renderSeq;
    return fetch(rootUrl() + '?sections=' + SECTION_ID, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        if (seq !== renderSeq) return;
        var html = data && data[SECTION_ID];
        if (typeof html !== 'string') return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var active = document.activeElement;
        var focusWasInUpsell = false;
        PARTS.forEach(function (part) {
          var source = doc.querySelector('[data-cm-part="' + part + '"]');
          if (!source) return;
          Array.prototype.forEach.call(document.querySelectorAll('[data-cm-part="' + part + '"]'), function (mount) {
            if (part === 'upsell' && active && mount.contains(active)) focusWasInUpsell = mount;
            Array.prototype.forEach.call(mount.querySelectorAll('[data-cm-upsell], [data-cm-gift]'), function (old) {
              if (old.__cmObserver) old.__cmObserver.disconnect();
            });
            mount.innerHTML = source.innerHTML;
          });
        });
        initAll();
        if (focusWasInUpsell) restoreFocus(focusWasInUpsell);
        if (focusWasInGift(active)) restoreGiftFocus();
      })
      .catch(function () {
        // Leave the current markup; the next change re-renders.
      });
  }

  // The focused card was replaced: move focus to the same mount's first card, else to the
  // drawer's close button, never to the page body.
  function restoreFocus(mount) {
    var target = mount.querySelector('[data-cm-add]');
    if (!target) {
      var drawer = document.getElementById('CartDrawer');
      target = drawer && mount.closest('#CartDrawer') ? drawer.querySelector('.js-drawer-close') : null;
    }
    if (target) target.focus();
  }

  // ---------- Upsell row ----------

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = list[i];
      list[i] = list[j];
      list[j] = t;
    }
    return list;
  }

  function initUpsell(root) {
    if (root.__cmInit) return;
    root.__cmInit = true;
    var row = root.querySelector('.mmsc-upsell-products');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-cm-card]'));

    if (root.dataset.order === 'random') {
      var max = parseInt(root.dataset.maxShown, 10) || 5;
      shuffle(cards).forEach(function (card, i) {
        card.hidden = i >= max;
        row.appendChild(card);
      });
    }

    var left = root.querySelector('.mmsc-upsell-arrow--left');
    var right = root.querySelector('.mmsc-upsell-arrow--right');
    function syncArrows() {
      if (left) left.style.display = row.scrollLeft <= 0 ? 'none' : 'flex';
      if (right) right.style.display = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1 ? 'none' : 'flex';
    }
    if (left) left.addEventListener('click', function () { row.scrollBy({ left: -200, behavior: 'smooth' }); });
    if (right) right.addEventListener('click', function () { row.scrollBy({ left: 200, behavior: 'smooth' }); });
    row.addEventListener('scroll', syncArrows, { passive: true });
    syncArrows();

    cards.forEach(function (card) { bindCard(card, root); });
    armImpression(root);
  }

  function bindCard(card, root) {
    var btn = card.querySelector('[data-cm-add]');
    var view = card.querySelector('[data-cm-view]');
    var errorEl = card.querySelector('.mmsc-upsell-card-error');
    var variantId = Number(card.dataset.variantId);
    var handle = card.dataset.quickLookHandle;
    var offer = root.dataset.offer;

    if (btn) {
      btn.addEventListener('click', function () {
        btn.disabled = true;
        btn.textContent = 'Adding…';
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        addFromBar(variantId, offer, card).catch(function (err) {
          if (errorEl) {
            errorEl.textContent = (err && err.message) || GENERIC_ERROR;
            errorEl.hidden = false;
          }
          btn.disabled = false;
          btn.textContent = 'Add';
        });
      });
    }

    if (view) {
      view.addEventListener('click', function () {
        if (!window.MMSQuickLook) {
          window.location.href = rootUrl() + 'products/' + encodeURIComponent(handle);
          return;
        }
        window.MMSQuickLook.open(handle, {
          opener: view,
          variantId: variantId,
          action: {
            label: 'Add to cart',
            run: function () { return addFromBar(variantId, offer, card); }
          }
        });
      });
    }
  }

  // ---------- Add (one request, then an "add" event) ----------

  function addFromBar(variantId, offerHandle, card) {
    var C = window.MMSCart;
    if (!C) return Promise.reject(new Error(GENERIC_ERROR));
    C.beginBusy();
    var done = function () { C.endBusy(); };
    var root = card.closest('[data-cm-upsell]');
    var totalBefore = null;
    return C.getCart()
      .then(function (cart) {
        totalBefore = cart && typeof cart.total_price === 'number' ? cart.total_price : null;
        return C.addItems([{ id: variantId, quantity: 1 }]);
      })
      .then(function () {
        // Attribution for add-on reporting: one "add" event, with the cart totals at the
        // time of the add (the bar's remaining amount as rendered for this cart).
        var events = C.sendAddEvents([{
          surface: 'cart',
          rule_handle: offerHandle,
          variant_id: Number(variantId),
          product_id: Number(card.dataset.productId) || undefined,
          quantity: 1,
          cart_total_cents: totalBefore != null ? totalBefore : (Number(root && root.dataset.totalCents) || 0),
          remaining_cents: Number(root && root.dataset.remainingCents) || 0
        }], { url: root && root.dataset.impressionUrl });
        try {
          if (typeof window.gtag === 'function') {
            var price = Number(card.dataset.price) / 100;
            var titleEl = card.querySelector('.mmsc-upsell-card-title');
            var title = titleEl ? titleEl.textContent : '';
            window.gtag('event', 'upsell_add_to_cart', {
              currency: 'USD',
              value: price,
              items: [{ item_name: title, price: price, quantity: 1 }]
            });
          }
        } catch (e) {
          // ignore
        }
        done();
        // On /cart, an add from the page's own row: the page's line items are server-rendered,
        // so reload it (as Impulse and the add-on picker do on the cart page), once the add
        // event has left (sendAddEvents waits at most ~1.5 s).
        if (document.getElementById('CartPageForm') && !card.closest('#CartDrawer')) {
          return events.then(function () { window.location.reload(); });
        }
        // Impulse redraws its line items; mms:cart-changed (from the observed add) re-renders the bar.
        document.dispatchEvent(new CustomEvent('cart:build'));
      }, function (err) {
        done();
        var message = err && (err.type === 'cart' ? err.description : null);
        if (!message && err && err.type === 'network') message = 'Couldn’t reach the store. Please try again.';
        throw new Error(message || GENERIC_ERROR);
      });
  }

  // ---------- Gift panel ----------
  // Choose mode: a bar (gift icon, message, chevron) that opens the picker's rows. The
  // open/closed state survives re-renders (per page). Claim adds the sample through
  // MMSCart.addItems with _mms_gift "1", then sends one "add" event (surface gift, rule
  // "gift"); Change (a sample is already in the cart) removes every sample line, then adds
  // the new one, in one guarded sequence. The enforcement in assets/mms-cart.js keeps the
  // cart within the rules after every change.

  var giftOpen = false;
  var giftFocusVariant = null;

  function focusWasInGift(active) {
    return !!(active && active.closest && active.closest('[data-cm-part="gift"]'));
  }

  function restoreGiftFocus() {
    var mounts = document.querySelectorAll('[data-cm-part="gift"]');
    for (var i = 0; i < mounts.length; i++) {
      var mount = mounts[i];
      if (!mount.offsetParent) continue;
      var target = (giftFocusVariant && mount.querySelector('[data-cm-gift-row][data-variant-id="' + giftFocusVariant + '"] .mmsc-freegift-btn'))
        || mount.querySelector('[data-cm-gift-toggle]');
      if (target && typeof target.focus === 'function') {
        if (!target.hasAttribute('tabindex') && target.tagName === 'SPAN') target.setAttribute('tabindex', '-1');
        target.focus();
        return;
      }
    }
  }

  function setGiftOpen(root, open) {
    var toggle = root.querySelector('[data-cm-gift-toggle]');
    var list = root.querySelector('[data-cm-gift-list]');
    if (!toggle || !list) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    list.hidden = !open;
    root.classList.toggle('is-open', open);
  }

  function initGift(root) {
    if (root.__cmInit) return;
    root.__cmInit = true;
    var toggle = root.querySelector('[data-cm-gift-toggle]');
    if (toggle) {
      setGiftOpen(root, giftOpen);
      toggle.addEventListener('click', function () {
        giftOpen = toggle.getAttribute('aria-expanded') !== 'true';
        Array.prototype.forEach.call(document.querySelectorAll('[data-cm-gift]'), function (other) {
          setGiftOpen(other, giftOpen);
        });
        if (giftOpen) armGiftImpression(root);
      });
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-cm-gift-row]'), function (row) {
      var btn = row.querySelector('[data-cm-gift-claim]');
      if (!btn) return;
      var errorEl = row.querySelector('.mmsc-freegift-error');
      btn.addEventListener('click', function () {
        var label = btn.textContent;
        var variantId = Number(row.dataset.variantId);
        giftFocusVariant = String(variantId);
        Array.prototype.forEach.call(root.querySelectorAll('[data-cm-gift-claim]'), function (b) { b.disabled = true; });
        btn.textContent = label === 'Change' ? 'Changing…' : 'Adding…';
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        claimGift(variantId, root, row.dataset.productId).catch(function (err) {
          if (errorEl) {
            errorEl.textContent = (err && err.message) || GENERIC_GIFT_ERROR;
            errorEl.hidden = false;
          }
          Array.prototype.forEach.call(root.querySelectorAll('[data-cm-gift-claim]'), function (b) { b.disabled = false; });
          btn.textContent = label;
        });
      });
    });
    armGiftImpression(root);
  }

  var GENERIC_GIFT_ERROR = 'Sorry, this sample couldn’t be added.';

  function claimGift(variantId, root, productId) {
    var C = window.MMSCart;
    if (!C) return Promise.reject(new Error(GENERIC_GIFT_ERROR));
    C.beginBusy();
    var done = function () { C.endBusy(); };
    var totalBefore = null;
    return C.getCart()
      .then(function (cart) {
        totalBefore = cart && typeof cart.total_price === 'number' ? cart.total_price : null;
        // Change: the sample lines already in the cart go first.
        var updates = {};
        (cart.items || []).forEach(function (item) {
          if (C.isSample(item)) updates[item.key] = 0;
        });
        return Object.keys(updates).length ? C.updateCart(updates, null) : cart;
      })
      .then(function () {
        return C.addItems([{ id: variantId, quantity: 1, properties: { _mms_gift: '1' } }]);
      })
      .then(function () {
        // Attribution for add-on reporting: one "add" event for the claimed sample (gift
        // lines are also attributed by their _mms_gift property).
        var events = C.sendAddEvents([{
          surface: 'gift',
          rule_handle: 'gift',
          variant_id: Number(variantId),
          product_id: Number(productId) || undefined,
          quantity: 1,
          cart_total_cents: totalBefore != null ? totalBefore : (Number(root.dataset.totalCents) || 0)
        }], { url: root.dataset.impressionUrl });
        done();
        // On /cart, a claim from the page's own panel: the page's line items are
        // server-rendered, so reload it (as the upsell bar does), once the add event has left.
        if (document.getElementById('CartPageForm') && !root.closest('#CartDrawer')) {
          return events.then(function () { window.location.reload(); });
        }
        // Impulse redraws its line items; mms:cart-changed (from the observed writes) re-renders the panel.
        document.dispatchEvent(new CustomEvent('cart:build'));
      }, function (err) {
        done();
        // A Change whose add failed has removed the old sample: redraw so the cart shows it.
        document.dispatchEvent(new CustomEvent('cart:build'));
        var message = err && (err.type === 'cart' ? err.description : null);
        if (!message && err && err.type === 'network') message = 'Couldn’t reach the store. Please try again.';
        throw new Error(message || GENERIC_GIFT_ERROR);
      });
  }

  // Impression (surface "gift"): once per browser session, when the picker with claimable
  // samples is at least 50% visible. The payload's ids are the claimable samples, in order.
  var GIFT_IMPRESSION_KEY = 'gift';

  function armGiftImpression(root) {
    if (!root.dataset.impressionUrl || !root.dataset.shownIds) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (typeof IntersectionObserver !== 'function' || impressionSent(GIFT_IMPRESSION_KEY)) return;
    if (root.__cmObserver) return;
    try {
      root.__cmObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) sendGiftImpression(root);
        });
      }, { threshold: 0.5 });
      root.__cmObserver.observe(root);
    } catch (e) {
      // ignore
    }
  }

  function sendGiftImpression(root) {
    if (root.__cmObserver) root.__cmObserver.disconnect();
    if (!root.isConnected || impressionSent(GIFT_IMPRESSION_KEY) || !window.MMSCart) return;
    var shown = String(root.dataset.shownIds || '').split(',')
      .map(function (id) { return Number(id); })
      .filter(function (id) { return id > 0; })
      .slice(0, 24);
    if (!shown.length) return;
    markImpressionSent(GIFT_IMPRESSION_KEY);
    window.MMSCart.sendImpression(root.dataset.impressionUrl, {
      surface: 'gift',
      offer_handle: 'gift',
      shown_product_ids: shown,
      cart_total_cents: Number(root.dataset.totalCents) || 0,
      remaining_cents: Number(root.dataset.remainingCents) || 0
    });
  }

  // ---------- Impressions ----------

  function impressionSent(offer) {
    if (sentThisPage[offer]) return true;
    try {
      return window.sessionStorage.getItem(IMPRESSION_KEY + offer) === '1';
    } catch (e) {
      return false;
    }
  }

  function markImpressionSent(offer) {
    sentThisPage[offer] = true;
    try {
      window.sessionStorage.setItem(IMPRESSION_KEY + offer, '1');
    } catch (e) {
      // sessionStorage unavailable: once per page instead.
    }
  }

  function armImpression(root) {
    var offer = root.dataset.offer;
    if (!offer || !root.dataset.impressionUrl) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (typeof IntersectionObserver !== 'function' || impressionSent(offer)) return;
    try {
      root.__cmObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) sendImpression(root);
        });
      }, { threshold: 0.5 });
      root.__cmObserver.observe(root);
    } catch (e) {
      // ignore
    }
  }

  function sendImpression(root) {
    var offer = root.dataset.offer;
    if (root.__cmObserver) root.__cmObserver.disconnect();
    if (!root.isConnected || impressionSent(offer) || !window.MMSCart) return;
    var shown = Array.prototype.slice.call(root.querySelectorAll('[data-cm-card]'))
      .filter(function (card) { return !card.hidden; })
      .map(function (card) { return Number(card.dataset.productId); })
      .slice(0, 24);
    if (!shown.length) return;
    markImpressionSent(offer);
    window.MMSCart.sendImpression(root.dataset.impressionUrl, {
      surface: 'cart',
      offer_handle: offer,
      shown_product_ids: shown,
      cart_total_cents: Number(root.dataset.totalCents) || 0,
      remaining_cents: Number(root.dataset.remainingCents) || 0
    });
  }

  // ---------- Boot ----------

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cm-upsell]'), initUpsell);
    Array.prototype.forEach.call(document.querySelectorAll('[data-cm-gift]'), initGift);
  }

  document.addEventListener('mms:cart-changed', function () { render(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.MMSCartMilestones = Object.freeze({ render: render });
})();

// Synced with the add-event attribution change (fa9b838).
